import {ChildrenProps} from '@app/helpers/react';
import {
  createContext,
  forwardRef,
  LabelHTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import {mergeRefs} from 'react-merge-refs';
import styled from 'styled-components';

import {Throw} from '../../helpers/exceptions';

interface Context {
  id: string | undefined;
  setId: (id: string | undefined) => void;
}

const context = createContext<Context | undefined>(undefined);

const Div = styled.div`
  display: inline-block;
`;

const RawLabel = styled.label`
  display: inline-block;
  cursor: inherit;
`;

interface LabelProviderProps {
  children: (id?: string) => ReactNode;
}

const LabelProvider = ({children}: LabelProviderProps) => {
  const [id, setId] = useState<string>();
  const contextValue = useMemo(() => ({id, setId}), [id]);
  return (
    <context.Provider value={contextValue}>{children(id)}</context.Provider>
  );
};

export const LabelResetProvider = ({children}: ChildrenProps) => (
  <context.Provider value={undefined}>{children}</context.Provider>
);

/**
 * Allows a component to get an id that will be inserted into the for attribute of the containing label
 * @returns string
 */
export const useLabelId = () => {
  const {id, setId} =
    useContext(context) ??
    Throw(new Error('useLabelId can only be used within a Label component'));

  const candidateId = useId();

  useEffect(() => {
    if (id === undefined) {
      setId(candidateId);
    } else if (id !== candidateId) {
      throw new Error('another component is already using this label id');
    }
    return () => setId(undefined);
  }, []);

  return candidateId;
};

const useInLabel = () => !!useContext(context);

interface Props extends Omit<LabelHTMLAttributes<HTMLElement>, 'children'> {
  children?: ReactNode | ((labelId?: string) => ReactNode);
}

const UseLabelId = ({children}: {children: (forId?: string) => ReactNode}) => (
  <>{children(useLabelId())}</>
);

/**
 * Allows nesting of labels, internal labels will become divs instead
 */
const Label = forwardRef<HTMLElement, Props>(
  ({children, form, htmlFor, ...rest}, ref) => {
    const inLabel = useInLabel();

    const contraRef = useMemo(() => mergeRefs([ref]), [ref]);

    const content =
      typeof children === 'function' ? (
        htmlFor ? (
          children(htmlFor)
        ) : (
          <UseLabelId>{(id) => children(id)}</UseLabelId>
        )
      ) : (
        children
      );

    if (inLabel) {
      return (
        <Div ref={contraRef} {...rest}>
          {content}
        </Div>
      );
    } else {
      return (
        <LabelProvider>
          {(id) => (
            <RawLabel
              ref={contraRef}
              form={form}
              htmlFor={htmlFor ?? id}
              {...rest}
            >
              {content}
            </RawLabel>
          )}
        </LabelProvider>
      );
    }
  }
);

export default Label;
